// TODO: remove this file if this file replaces the default CustomFileInput
/* istanbul ignore file */
import type { ChangeEvent } from 'react';
import React, { useCallback } from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { useClearUppyInput } from './hooks/useClearUppyInput';

type Props = {
	uppy: Uppy<Meta, Body>;
	fileInputId: string;
	onButtonClick?: () => void;
};

const AccessibleFileInputStyles = {
	// hide the input
	border: '0',
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	display: 'block',
	height: '1px',
	width: '1px',
	padding: '0',
	overflow: 'hidden',
	position: 'absolute' as const,
	whitespace: 'nowrap',
};

export const CustomFileInput: React.FC<Props> = ({ uppy, fileInputId, onButtonClick }) => {
	const inputFileRef = React.useRef<HTMLInputElement>(null);
	useClearUppyInput({ uppy, inputFileRef });

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Space' || e.key === ' ') {
				// prevent default scrolling behavior
				e.preventDefault();
				inputFileRef.current?.click();
			}
		},
		[inputFileRef],
	);

	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { files } = event.target;
			if (files) {
				const filesArr = Array.from(files);
				filesArr.forEach((file) => {
					try {
						// pass encountered errors and
						// use source matching original Uppy FileInput
						uppy.addFile({
							source: 'react:FileInput',
							name: file.name,
							type: file.type,
							data: file,
						});
					} catch (e) {
						// ignore errors here, as they are handled in the uppy abstraction
					}
				});
			}
		},
		[uppy],
	);

	// Using 'id' for accessibility since wrapping with a plain label
	// results in a file input bug iOS < 14, but is resolved using htmlFor.
	// also, none of the APIs on Uppy to set id are available for versions, 1.2x
	return (
		<input
			ref={inputFileRef}
			type="file"
			onChange={handleFileChange}
			onClick={onButtonClick}
			id={fileInputId}
			data-testid={fileInputId}
			onKeyDown={handleKeyDown}
			accept="image/*"
			sx={{ ...AccessibleFileInputStyles }}
		/>
	);
};
